import React, { useEffect, useState, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { useAuth } from '../hooks/useAuth';
import { vehiclesApi, VehicleHealthEntry, OBDHealth } from '../api/vehicles.api';
import { listSmartcarConnections, SmartcarConnection } from '../api/smartcar.api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type OverallStatus = 'critical' | 'warning' | 'healthy';

// ---------------------------------------------------------------------------
// Pure helpers — no hooks, safe to use at module level
// ---------------------------------------------------------------------------

function computeOverallStatus(
  health: OBDHealth | null,
  smartcar?: SmartcarConnection
): OverallStatus {
  // Check Smartcar fuel first (critical threshold)
  const scFuel = smartcar?.last_data?.fuel_percent ?? null;

  if (scFuel !== null && scFuel < 10) return 'critical';

  if (!health) {
    // No OBD — still check Smartcar warning threshold
    if (scFuel !== null && scFuel < 25) return 'warning';
    return 'healthy';
  }

  const { dtc_count, battery_voltage, coolant_temp, service_distance, fuel_level } = health;

  // Critical conditions
  if (
    (dtc_count !== null && dtc_count > 0) ||
    (battery_voltage !== null && battery_voltage < 12) ||
    (coolant_temp !== null && coolant_temp > 105) ||
    (service_distance !== null && service_distance < 500)
  ) {
    return 'critical';
  }

  // Warning conditions
  if (
    (battery_voltage !== null && battery_voltage < 13.5) ||
    (service_distance !== null && service_distance < 2000) ||
    (fuel_level !== null && fuel_level < 25)
  ) {
    return 'warning';
  }

  // OBD fuel is null — fall back to Smartcar warning threshold
  if (fuel_level === null && scFuel !== null && scFuel < 25) return 'warning';

  return 'healthy';
}

function isOffline(health: OBDHealth | null, smartcar?: SmartcarConnection): boolean {
  // A vehicle with recent Smartcar data (within 48 h) is NOT offline
  const scFetchedAt = smartcar?.last_data?.fetched_at;
  if (scFetchedAt) {
    const scAge = Date.now() - new Date(scFetchedAt).getTime();
    if (scAge <= 48 * 60 * 60 * 1000) return false;
  }

  if (!health || !health.updated_at) return true;
  const updatedAt = new Date(health.updated_at).getTime();
  return Date.now() - updatedAt > 24 * 60 * 60 * 1000; // older than 24 h
}

/**
 * Decode hex-encoded DTC string into an array of OBD-II codes.
 * Each DTC is 5 chars = 10 hex chars (e.g. "5030333830" → ["P0380"]).
 * Multiple codes are stored concatenated (e.g. "50303338305030333831" → ["P0380", "P0381"]).
 */
function decodeDtcCodes(raw: string | null): string[] {
  if (!raw) return [];
  try {
    // Each DTC = 5 ASCII chars = 10 hex chars
    const chunks = raw.match(/.{10}/g) ?? [raw];
    const codes = chunks.map(chunk => {
      const pairs = chunk.match(/.{2}/g);
      if (!pairs) return null;
      return pairs.map(b => String.fromCharCode(parseInt(b, 16))).join('');
    }).filter((c): c is string => !!c && /^[PCBU][0-9A-F]/i.test(c));
    return codes.length > 0 ? codes : [raw];
  } catch {
    return [raw];
  }
}

function formatRelativeTime(dateString: string): string {
  const diff = Date.now() - new Date(dateString).getTime();
  const minutes = Math.floor(diff / 60_000);
  const hours = Math.floor(diff / 3_600_000);
  const days = Math.floor(diff / 86_400_000);

  if (minutes < 1) return "à l'instant";
  if (minutes < 60) return `il y a ${minutes} min`;
  if (hours < 24) return `il y a ${hours} h`;
  return `il y a ${days} j`;
}

// Extract string ID from vehicle_id which may be a string or a populated object
function resolveVehicleId(raw: unknown): string {
  if (!raw) return '';
  if (typeof raw === 'string') return raw;
  if (typeof raw === 'object' && raw !== null && '_id' in raw) {
    return String((raw as { _id: unknown })._id);
  }
  return String(raw);
}

// ---------------------------------------------------------------------------
// Sub-components (OBD — unchanged)
// ---------------------------------------------------------------------------

const StatusDot: React.FC<{ status: OverallStatus }> = ({ status }) => {
  const colors: Record<OverallStatus, string> = {
    critical: 'bg-red-500',
    warning: 'bg-amber-400',
    healthy: 'bg-green-500',
  };
  return (
    <span
      className={`inline-block w-3 h-3 rounded-full flex-shrink-0 ${colors[status]}`}
      aria-label={status}
    />
  );
};

interface MetricRowProps {
  label: string;
  children: React.ReactNode;
}

const MetricRow: React.FC<MetricRowProps> = ({ label, children }) => (
  <div className="flex items-center justify-between py-1.5 border-b border-gray-100 last:border-0">
    <span className="text-xs text-gray-500 font-medium">{label}</span>
    <span className="text-xs font-semibold ml-2 text-right">{children}</span>
  </div>
);

interface BatteryValueProps {
  voltage: number | null;
}

const BatteryValue: React.FC<BatteryValueProps> = ({ voltage }) => {
  if (voltage === null) return <span className="text-gray-400">N/A</span>;
  const color =
    voltage >= 13.5 ? 'text-green-600' : voltage >= 12 ? 'text-amber-600' : 'text-red-600';
  return <span className={color}>{voltage.toFixed(1)} V</span>;
};

interface DtcValueProps {
  dtcCount: number | null;
  dtcCodes: string | null;
}

const DtcValue: React.FC<DtcValueProps> = ({ dtcCount, dtcCodes }) => {
  if (dtcCount === null) return <span className="text-gray-400">N/A</span>;
  if (dtcCount === 0) return <span className="text-green-600 font-semibold">OK</span>;
  const codes = decodeDtcCodes(dtcCodes);
  return (
    <span className="inline-flex flex-wrap items-center gap-1 justify-end">
      {codes.map(code => (
        <span
          key={code}
          className="bg-red-100 text-red-700 font-mono text-xs font-bold px-1.5 py-0.5 rounded"
          title={code}
        >
          {code}
        </span>
      ))}
    </span>
  );
};

interface ServiceDistanceValueProps {
  km: number | null;
}

const ServiceDistanceValue: React.FC<ServiceDistanceValueProps> = ({ km }) => {
  if (km === null) return <span className="text-gray-400">N/A</span>;
  const color =
    km < 500 ? 'text-red-600' : km < 2000 ? 'text-amber-600' : 'text-green-600';
  return <span className={color}>{km.toLocaleString('fr-FR')} km</span>;
};

interface CoolantValueProps {
  temp: number | null;
}

const CoolantValue: React.FC<CoolantValueProps> = ({ temp }) => {
  if (temp === null) return <span className="text-gray-400">N/A</span>;
  const color =
    temp < 50 ? 'text-gray-400' : temp <= 105 ? 'text-green-600' : 'text-red-600';
  return <span className={color}>{temp} °C</span>;
};

interface FuelBarProps {
  level: number | null;
}

const FuelBar: React.FC<FuelBarProps> = ({ level }) => {
  if (level === null) return <span className="text-gray-400">N/A</span>;
  const pct = Math.max(0, Math.min(100, level));
  const barColor =
    pct < 10 ? 'bg-red-500' : pct < 25 ? 'bg-amber-400' : 'bg-green-500';
  const textColor =
    pct < 10 ? 'text-red-600' : pct < 25 ? 'text-amber-600' : 'text-green-600';
  return (
    <span className="flex items-center gap-2 w-full justify-end">
      <span className={`text-xs font-bold ${textColor}`}>{pct}%</span>
      <span className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden inline-block flex-shrink-0">
        <span
          className={`h-full rounded-full block ${barColor}`}
          style={{ width: `${pct}%` }}
        />
      </span>
    </span>
  );
};

interface OilTempValueProps {
  temp: number | null;
}

const OilTempValue: React.FC<OilTempValueProps> = ({ temp }) => {
  if (temp === null) return <span className="text-gray-400">N/A</span>;
  const color = temp > 130 ? 'text-red-600' : 'text-green-600';
  return <span className={color}>{temp} °C</span>;
};

interface RpmValueProps {
  rpm: number | null;
}

const RpmValue: React.FC<RpmValueProps> = ({ rpm }) => {
  if (rpm === null) return <span className="text-gray-400">N/A</span>;
  const color = rpm > 4000 ? 'text-amber-600' : 'text-gray-700';
  return <span className={color}>{rpm.toLocaleString('fr-FR')} tr/min</span>;
};

interface IgnitionValueProps {
  ignition: number | null;
  speed: number | null;
}

const IgnitionValue: React.FC<IgnitionValueProps> = ({ ignition, speed }) => {
  if (ignition === null) return <span className="text-gray-400">N/A</span>;
  if (ignition === 1) {
    return (
      <span className="inline-flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-full bg-green-500 inline-block"></span>
        <span className="text-green-700 font-semibold">ON{speed !== null && speed > 0 ? ` · ${speed} km/h` : ''}</span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="w-2 h-2 rounded-full bg-gray-400 inline-block"></span>
      <span className="text-gray-500">OFF</span>
    </span>
  );
};

// ---------------------------------------------------------------------------
// Smartcar sub-components
// ---------------------------------------------------------------------------

type ChargeState = 'CHARGING' | 'FULLY_CHARGED' | 'NOT_CHARGING';

const ChargeStateBadge: React.FC<{ state: ChargeState | string }> = ({ state }) => {
  if (state === 'FULLY_CHARGED') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
        Plein
      </span>
    );
  }
  if (state === 'CHARGING') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
        En charge
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-500">
      Non branché
    </span>
  );
};

interface SmartcarSectionProps {
  smartcar: SmartcarConnection;
}

const SmartcarSection: React.FC<SmartcarSectionProps> = ({ smartcar }) => {
  const d = smartcar.last_data;
  if (!d) return null;

  return (
    <div className="px-4 pt-2 pb-3">
      {/* Divider with label */}
      <div className="flex items-center gap-2 mb-2">
        <span className="flex-1 h-px bg-indigo-100" />
        <span className="text-xs font-semibold text-indigo-500 tracking-wide uppercase">
          Smartcar
        </span>
        <span className="flex-1 h-px bg-indigo-100" />
      </div>

      {d.odometer_km !== undefined && (
        <MetricRow label="Odomètre SC">
          <span className="text-gray-700">
            {Math.round(d.odometer_km).toLocaleString('fr-FR')} km
          </span>
        </MetricRow>
      )}

      {d.fuel_percent !== undefined && (
        <MetricRow label="Carburant SC">
          <FuelBar level={d.fuel_percent} />
        </MetricRow>
      )}

      {d.battery_percent !== undefined && (
        <MetricRow label="Batterie SC">
          <FuelBar level={d.battery_percent} />
        </MetricRow>
      )}

      {d.battery_range_km !== undefined && (
        <MetricRow label="Autonomie">
          <span className="text-gray-700">
            {Math.round(d.battery_range_km).toLocaleString('fr-FR')} km
          </span>
        </MetricRow>
      )}

      {d.charge_state !== undefined && (
        <MetricRow label="État de charge">
          <ChargeStateBadge state={d.charge_state} />
        </MetricRow>
      )}

      {d.fetched_at && (
        <MetricRow label="Sync SC">
          <span className="text-gray-500">{formatRelativeTime(d.fetched_at)}</span>
        </MetricRow>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Vehicle card
// ---------------------------------------------------------------------------

interface VehicleCardProps {
  vehicle: VehicleHealthEntry;
  smartcar?: SmartcarConnection;
}

const VehicleCard: React.FC<VehicleCardProps> = ({ vehicle, smartcar }) => {
  const health = vehicle.obd_health;
  const offline = isOffline(health, smartcar);
  const status = offline ? 'healthy' : computeOverallStatus(health, smartcar);

  if (offline) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col">
        {/* Card header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <div className="min-w-0">
            <p className="text-sm font-bold text-gray-900 truncate">{vehicle.plate_number}</p>
            <p className="text-xs text-gray-500 truncate">
              {vehicle.brand} {vehicle.model}
            </p>
          </div>
          <span className="inline-block w-3 h-3 rounded-full bg-gray-300 flex-shrink-0" />
        </div>

        {/* Offline state */}
        <div className="flex-1 flex flex-col items-center justify-center py-8 px-4 gap-2">
          <svg
            className="w-8 h-8 text-gray-300"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p className="text-sm font-semibold text-gray-400">Hors ligne</p>
          <p className="text-xs text-gray-400 text-center">
            Aucune donnée dans les dernières 24 h
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col">
      {/* Card header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div className="min-w-0">
          <p className="text-sm font-bold text-gray-900 truncate">{vehicle.plate_number}</p>
          <p className="text-xs text-gray-500 truncate">
            {vehicle.brand} {vehicle.model}
          </p>
        </div>
        <StatusDot status={status} />
      </div>

      {/* OBD Metrics */}
      <div className="px-4 py-3 flex-1">
        <MetricRow label="Batterie">
          <BatteryValue voltage={health?.battery_voltage ?? null} />
        </MetricRow>
        <MetricRow label="Moteur">
          <DtcValue dtcCount={health?.dtc_count ?? null} dtcCodes={health?.dtc_codes ?? null} />
        </MetricRow>
        <MetricRow label="Prochain entretien">
          <ServiceDistanceValue km={health?.service_distance ?? null} />
        </MetricRow>
        <MetricRow label="Température liquide">
          <CoolantValue temp={health?.coolant_temp ?? null} />
        </MetricRow>
        <MetricRow label="Carburant">
          <FuelBar level={health?.fuel_level ?? null} />
        </MetricRow>
        <MetricRow label="Huile moteur">
          <OilTempValue temp={health?.engine_oil_temp ?? null} />
        </MetricRow>
        <MetricRow label="Régime moteur">
          <RpmValue rpm={health?.rpm ?? null} />
        </MetricRow>
        <MetricRow label="Moteur">
          <IgnitionValue ignition={health?.ignition ?? null} speed={health?.last_speed ?? null} />
        </MetricRow>
      </div>

      {/* Smartcar section — only rendered when a connection exists */}
      {smartcar && <SmartcarSection smartcar={smartcar} />}

      {/* Footer */}
      <div className="px-4 py-2 border-t border-gray-100 bg-gray-50 rounded-b-lg">
        <p className="text-xs text-gray-400">
          <span className="font-medium text-gray-500">Dernière mise à jour : </span>
          {health?.updated_at ? formatRelativeTime(health.updated_at) : 'Inconnue'}
        </p>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Skeleton card for loading state
// ---------------------------------------------------------------------------

const SkeletonCard: React.FC = () => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-200 animate-pulse">
    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
      <div className="space-y-1.5">
        <div className="h-3.5 w-20 bg-gray-200 rounded" />
        <div className="h-3 w-28 bg-gray-200 rounded" />
      </div>
      <div className="w-3 h-3 rounded-full bg-gray-200" />
    </div>
    <div className="px-4 py-3 space-y-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="flex justify-between items-center">
          <div className="h-3 w-24 bg-gray-200 rounded" />
          <div className="h-3 w-16 bg-gray-200 rounded" />
        </div>
      ))}
    </div>
    <div className="px-4 py-2 border-t border-gray-100 bg-gray-50 rounded-b-lg">
      <div className="h-3 w-32 bg-gray-200 rounded" />
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Summary badge counts
// ---------------------------------------------------------------------------

interface SummaryBadgesProps {
  vehicles: VehicleHealthEntry[];
  smartcarMap: Map<string, SmartcarConnection>;
}

const SummaryBadges: React.FC<SummaryBadgesProps> = ({ vehicles, smartcarMap }) => {
  const counts = vehicles.reduce(
    (acc, v) => {
      const smartcar = smartcarMap.get(v.vehicle_id);

      if (isOffline(v.obd_health, smartcar)) {
        acc.offline++;
      } else {
        const s = computeOverallStatus(v.obd_health, smartcar);
        acc[s]++;
      }
      return acc;
    },
    { critical: 0, warning: 0, healthy: 0, offline: 0 }
  );

  return (
    <div className="flex flex-wrap gap-3">
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
        <span className="w-2 h-2 rounded-full bg-red-500 inline-block" />
        {counts.critical} critique{counts.critical !== 1 ? 's' : ''}
      </span>
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
        <span className="w-2 h-2 rounded-full bg-amber-400 inline-block" />
        {counts.warning} avertissement{counts.warning !== 1 ? 's' : ''}
      </span>
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
        <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
        {counts.healthy} sain{counts.healthy !== 1 ? 's' : ''}
      </span>
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-500">
        <span className="w-2 h-2 rounded-full bg-gray-400 inline-block" />
        {counts.offline} hors ligne
      </span>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export const MaintenancePage: React.FC = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.isSuperAdmin === true || user?.role === 'super_admin';

  const [vehicles, setVehicles] = useState<VehicleHealthEntry[]>([]);
  const [smartcarMap, setSmartcarMap] = useState<Map<string, SmartcarConnection>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadHealth = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Fetch OBD health and Smartcar connections in parallel.
      // Smartcar is supplemental — silently ignore its errors.
      const [obdData, scConnections] = await Promise.all([
        vehiclesApi.getFleetHealth(),
        listSmartcarConnections().catch(() => [] as SmartcarConnection[]),
      ]);

      setVehicles(Array.isArray(obdData) ? obdData : []);

      // Build vehicleId → SmartcarConnection map for O(1) lookup.
      // vehicle_id may be a raw string or a populated object (depends on backend version).
      const map = new Map<string, SmartcarConnection>();
      for (const conn of scConnections) {
        const id = resolveVehicleId(conn.vehicle_id);
        if (id) map.set(id, conn);
      }
      setSmartcarMap(map);
    } catch (err: unknown) {
      console.error('Failed to load fleet health:', err);
      setError("Impossible de charger les données de santé des véhicules.");
      setVehicles([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHealth();
  }, [loadHealth]);

  // Guard: only super_admin may access this page
  if (!isSuperAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Layout>
      <div className="p-6 max-w-screen-2xl mx-auto">
        {/* Page header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Maintenance — Santé des véhicules
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Vue d'ensemble de l'état OBD et Smartcar de toute la flotte
            </p>
          </div>

          <button
            onClick={loadHealth}
            disabled={isLoading}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
          >
            {/* Refresh icon */}
            <svg
              className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            Actualiser
          </button>
        </div>

        {/* Summary badges */}
        {!isLoading && vehicles.length > 0 && (
          <div className="mb-6">
            <SummaryBadges vehicles={vehicles} smartcarMap={smartcarMap} />
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="mb-6 flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
            <svg
              className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <div>
              <p className="text-sm font-semibold text-red-700">Erreur de chargement</p>
              <p className="text-sm text-red-600">{error}</p>
              <button
                onClick={loadHealth}
                className="mt-1 text-xs font-medium text-red-700 underline hover:no-underline"
              >
                Réessayer
              </button>
            </div>
          </div>
        )}

        {/* Loading grid */}
        {isLoading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !error && vehicles.length === 0 && (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <svg
              className="w-12 h-12 text-gray-300 mb-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437l1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008z"
              />
            </svg>
            <p className="text-base font-semibold text-gray-500">Aucun véhicule</p>
            <p className="text-sm text-gray-400 mt-1">
              Aucun véhicule avec données OBD disponible.
            </p>
          </div>
        )}

        {/* Vehicle grid */}
        {!isLoading && vehicles.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {vehicles.map((vehicle) => (
              <VehicleCard
                key={vehicle.vehicle_id}
                vehicle={vehicle}
                smartcar={smartcarMap.get(vehicle.vehicle_id)}
              />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};
